import { ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import VerticalLogo from 'src/assets/svgs/VerticalLogo';
import { Button } from 'src/components/system/Button';
import { PwaInstallBanner } from 'src/components/system/PwaInstallBanner';
import { IsMobileContext } from 'src/hooks/useMobile';
import { useAuthProfile } from 'src/hooks/useAuthProfile';
import { useCourseCategories } from 'src/contexts/CourseCategoriesContext';

import {
  CloseButton,
  Content,
  ContentWrapper,
  DrawerToggle,
  FooterBar,
  Layout,
  MobileOverlay,
  MobileTopbar,
  Nav,
  NavButton,
  NavItem,
  SidebarContainer,
  SidebarContent,
  Submenu,
  SubmenuItem,
  Topbar,
} from './styles';

interface SidebarProps {
  children: ReactNode;
}

export function Sidebar({ children }: SidebarProps) {
  const { isMobile } = useContext(IsMobileContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { profile, loading } = useAuthProfile();
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const closeDrawer = useCallback(() => {
    setIsDrawerOpen(false);
  }, []);
  const { categories } = useCourseCategories();

  const simulatorActive = useMemo(
    () => location.pathname.startsWith('/families') || location.pathname.startsWith('/simulate'),
    [location.pathname],
  );

  const certificationLinks = useMemo(
    () =>
      categories
        .filter(category => Boolean(category.slug))
        .map(category => ({
          label: category.fullName ?? category.name ?? category.slug,
          to: `/families/${category.slug}`,
        })),
    [categories],
  );

  const isAdmin = useMemo(() => (profile?.role ?? '').toLowerCase() === 'admin', [profile?.role]);

  const baseMenuItems = useMemo(
    () => [
      { key: 'home', label: 'Home', to: '/home' },
      {
        key: 'simulator',
        label: 'Simulador',
        submenu: certificationLinks,
      },
      { key: 'quick-game', label: 'Desafio do Toro', to: '/quick-game' },
      {
        key: 'profile',
        label: 'Meu painel',
        to: '/profile?tab=cadastro',
        submenu: [
          { label: 'Meu cadastro', to: '/profile?tab=cadastro' },
          { label: 'Plano de Melhoria', to: '/profile?tab=performance' },
        ],
      },
    ],
    [certificationLinks],
  );

  const menuItems = useMemo(
    () =>
      !loading && isAdmin
        ? [...baseMenuItems, { key: 'admin', label: 'Administração', to: '/admin' }]
        : baseMenuItems,
    [baseMenuItems, isAdmin, loading],
  );

  useEffect(() => {
    if (!isMobile) {
      closeDrawer();
    }
  }, [closeDrawer, isMobile]);

  useEffect(() => {
    if (isMobile) {
      closeDrawer();
    }

    if (simulatorActive) {
      setOpenSubmenu('simulator');
      return;
    }

    if (location.pathname.startsWith('/profile')) {
      setOpenSubmenu('profile');
      return;
    }

    setOpenSubmenu(null);
  }, [closeDrawer, isMobile, location.pathname, simulatorActive]);

  useEffect(() => {
    if (!isMobile || !isDrawerOpen) {
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsDrawerOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isDrawerOpen, isMobile]);

  useEffect(() => {
    if (!isMobile || typeof document === 'undefined') {
      return;
    }

    const { body } = document;
    const originalOverflow = body.style.overflow;
    body.style.overflow = isDrawerOpen ? 'hidden' : originalOverflow || '';

    return () => {
      body.style.overflow = originalOverflow;
    };
  }, [isDrawerOpen, isMobile]);

  function handleLogout() {
    try {
      localStorage.removeItem('token');
      localStorage.removeItem('profileFallback');
    } catch {
      // ignore storage errors
    }
    closeDrawer();
    navigate('/auth/login');
  }

  function toggleSubmenu(key: string) {
    setOpenSubmenu(prev => (prev === key ? null : key));
  }

  function isSubmenuOpen(key: string) {
    return openSubmenu === key;
  }

  const currentYear = useMemo(() => new Date().getFullYear(), []);

  return (
    <Layout isMobile={isMobile}>
      {isMobile && (
        <MobileTopbar>
          <DrawerToggle
            type="button"
            aria-label="Abrir menu lateral"
            onClick={() => setIsDrawerOpen(true)}
          >
            <span />
            <span />
            <span />
          </DrawerToggle>
          <VerticalLogo width={132} />
        </MobileTopbar>
      )}
      <SidebarContainer isMobile={isMobile} open={isMobile ? isDrawerOpen : true}>
        <Topbar isMobile={isMobile}>
          <VerticalLogo width={isMobile ? 148 : 165} />
          {isMobile && (
            <CloseButton
              type="button"
              aria-label="Fechar menu lateral"
              onClick={closeDrawer}
            >
              <span />
              <span />
            </CloseButton>
          )}
        </Topbar>
        <SidebarContent>
          <Nav>
            {menuItems.map(item => {
              if ('submenu' in item && item.submenu) {
                const open = isSubmenuOpen(item.key);
                return (
                  <div key={item.key}>
                    <NavButton
                      type="button"
                      onClick={() => toggleSubmenu(item.key)}
                      className={open ? 'active' : ''}
                      aria-expanded={open}
                      aria-controls={`submenu-${item.key}`}
                    >
                      {item.label}
                    </NavButton>
                    {open && (
                      <Submenu id={`submenu-${item.key}`}>
                        {item.submenu.map(link => (
                          <SubmenuItem
                            key={link.to}
                            to={link.to}
                            end
                            onClick={closeDrawer}
                          >
                            {link.label}
                          </SubmenuItem>
                        ))}
                      </Submenu>
                    )}
                  </div>
                );
              }

              return (
                <NavItem
                  key={item.key}
                  to={item.to!}
                  end
                  onClick={closeDrawer}
                >
                  {item.label}
                </NavItem>
              );
            })}
          </Nav>
          <div>
            <Button
              type="button"
              width={isMobile ? '100%' : '140px'}
              backgroundColor="#ffffff"
              borderColor="#222B82"
              buttonColor="#222B82"
              onClick={handleLogout}
              label={<span>Sair</span>}
            />
          </div>
        </SidebarContent>
      </SidebarContainer>
      {isMobile && isDrawerOpen && (
        <MobileOverlay type="button" aria-label="Fechar menu" onClick={closeDrawer} />
      )}
      <ContentWrapper>
        <PwaInstallBanner isMobile={isMobile} />
        <Content isMobile={isMobile}>{children}</Content>
        <FooterBar isMobile={isMobile}>
          <p>© {currentYear} Academia Rafael Toro</p>
        </FooterBar>
      </ContentWrapper>
    </Layout>
  );
}
